import { useState, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import { alpha, useTheme } from '@mui/material/styles'
import api from 'services/api'
import { DECISIONS, decisionOf } from '../StreamIngest/vocabulary'
import { outcomeInk, outcomeOf, routerHeardPath } from '../Moments/LedgerTable'
import { Line, Section } from '../Moments/MomentsSplit'
import { fmtStreamed, plural, useSeriesColors } from './vocabulary'

// One day of one user at the minute, on their own clock (their timezone, or
// UTC when the account has none the server can read), opened from a day of
// the overview. Three rows on one axis, from their midnight to the next (23
// or 25 hours across a clock change), each with its own key above it and,
// while the pointer is over any of them, what that row holds at the cursor
// under it; the cursor runs through all three.
//
// - Recording: every minute the phone declared, in the router's colors as
//   Stream Ingest draws them (speech found, none, not routed yet), or faded
//   red for a minute declared and never received. Where nothing is drawn,
//   nothing was recorded. How each recording ended is Stream Ingest's to
//   show, a span at a time: the day links there.
// - Moments: everything the selector found, a cut one green (its outcome's
//   ink, as the Moments page's chips) and full height, the rest grey. A quote
//   is seconds long and a pixel here is about two minutes, so a window draws
//   at least four.
// - Activity: a tick for each moment push sent, comment written and friend
//   added, in the overview's colors.
//
// Under the rows, everything the selector found (folded until opened) and
// what they did, in words.

const MIN_MOMENT_MINUTES = 4
// How near the pointer a moment or a tick reads as under it, in minutes.
const NEAR_MINUTES = 3

const EVENT_LABELS = {
  push: 'moment pushed',
  comment: 'commented',
  friend: 'added a friend',
}

// An instant on the user's clock: `HH:mm`, or the hour alone.
const clock = (ms, timezone, hourOnly = false) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    ...(hourOnly ? {} : { minute: '2-digit' }),
    hourCycle: 'h23',
  }).format(new Date(ms))

// The cursor's reading of an instant, in the 12-hour clock: `8:30 PM`.
const clock12 = (ms, timezone) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(ms))

// Every third hour on their clock, placed by the time elapsed since their
// midnight, so a day across a clock change keeps its true length and its
// ticks say which hour each is.
const hourTicks = (start, end, timezone) => {
  const ticks = []
  for (let at = start; at < end; at += 3_600_000) {
    const hour = clock(at, timezone, true)
    if (Number(hour) % 3 === 0) ticks.push({ x: (at - start) / 60_000, hour })
  }
  return ticks
}

// The recording row's four states, in the router's colors.
const recordingKey = (theme) => ({
  speech: { label: 'speech found', color: DECISIONS.route.color },
  silent: { label: 'no speech', color: DECISIONS.skip.color },
  waiting: {
    label: 'not routed yet',
    color: alpha(theme.palette.text.secondary, 0.3),
  },
  lost: {
    label: 'never received',
    color: alpha(theme.palette.error.main, 0.45),
  },
})

// Which of the four a minute is. A minute the router could not judge (gone
// before it ran, or a gate that broke) reads as routed or not as it was.
const stateOf = (minute) => {
  if (!minute.received) return 'lost'
  if (minute.decision == null) return 'waiting'
  return decisionOf(minute) === DECISIONS.route ? 'speech' : 'silent'
}

// Neighbouring minutes of one state draw as one rect.
const runs = (spans, dayStart, dayMinutes) => {
  const out = []
  spans
    .flatMap((span) => span.segments)
    .sort((a, b) => a.start_ms - b.start_ms)
    .forEach((minute) => {
      const state = stateOf(minute)
      const x = Math.max(0, (minute.start_ms - dayStart) / 60_000)
      const end = Math.min(
        dayMinutes,
        (minute.start_ms + minute.duration_ms - dayStart) / 60_000
      )
      const last = out[out.length - 1]
      if (last && last.state === state && Math.abs(last.end - x) < 0.01) {
        last.end = end
      } else {
        out.push({ x, end, state })
      }
    })
  return out
}

const Key = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, rowGap: 0.5 }}>
    {items.map(({ label, color, tick }) => (
      <Box
        key={label}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11 }}
      >
        <Box
          sx={{
            width: tick ? 2 : 10,
            height: 10,
            bgcolor: color,
            borderRadius: tick ? 0 : '2px',
          }}
        />
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {label}
        </Box>
      </Box>
    ))}
  </Box>
)

// One row: its name and key, its lane, and what it holds at the cursor. The
// lane reports the pointer as minutes into the day; every row draws the same
// cursor, so the three read as one.
const Row = ({
  title,
  items,
  height,
  drawn,
  hover,
  onHover,
  reading,
  children,
}) => {
  const theme = useTheme()
  const move = (event) => {
    const box = event.currentTarget.getBoundingClientRect()
    if (!box.width) return
    const fraction = (event.clientX - box.left) / box.width
    onHover(Math.min(1, Math.max(0, fraction)) * drawn.dayMinutes)
  }
  return (
    <Box sx={{ mt: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 0.5,
          mb: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Key items={items} />
      </Box>
      <Box
        onMouseMove={move}
        onMouseLeave={() => onHover(null)}
        sx={{ position: 'relative', height, cursor: 'crosshair' }}
      >
        <svg
          viewBox={`0 0 ${drawn.dayMinutes} ${height}`}
          preserveAspectRatio="none"
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          {[...drawn.ticks, { x: drawn.dayMinutes }].map((tick) => (
            <line
              key={tick.x}
              x1={tick.x}
              x2={tick.x}
              y1={0}
              y2={height}
              stroke={theme.palette.divider}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {children}
        </svg>
        {hover != null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(hover / drawn.dayMinutes) * 100}%`,
              width: '1px',
              bgcolor: 'text.primary',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
      <Box sx={{ minHeight: 18 }}>{reading && <Line>{reading}</Line>}</Box>
    </Box>
  )
}

const UserDay = ({
  userId,
  username,
  day,
  days,
  timezone,
  refreshes,
  onDay,
}) => {
  const theme = useTheme()
  const colors = useSeriesColors()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [hover, setHover] = useState(null)
  // Carried from one day to the next, as the moments page carries its folds.
  const [foundOpen, setFoundOpen] = useState(false)
  const [didOpen, setDidOpen] = useState(true)

  useEffect(() => {
    let live = true
    setError(null)
    api.streamMetrics
      .getUserDay({ userId, day })
      .then((next) => live && setData(next))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [userId, day, refreshes])

  const index = days.findIndex((row) => row.day === day)
  const shown = data?.day === day ? data : null
  const zone = shown?.timezone || timezone || 'UTC'
  const recording = useMemo(() => recordingKey(theme), [theme])
  const eventColor = {
    push: colors.moments,
    comment: colors.comments,
    friend: colors.friends,
  }
  const cutColor = outcomeInk(theme, 'cut')
  const notCut = alpha(theme.palette.text.secondary, 0.5)

  const drawn = useMemo(() => {
    if (!shown) return null
    const start = shown.start_ms
    const dayMinutes = (shown.end_ms - start) / 60_000
    const minuteOf = (ms) => (ms - start) / 60_000
    const candidates = shown.candidates.map((row) => {
      const from = Math.max(0, minuteOf(row.start_ms))
      const to = Math.min(dayMinutes, minuteOf(row.end_ms))
      const width = Math.max(MIN_MOMENT_MINUTES, to - from)
      return { ...row, x: (from + to) / 2 - width / 2, width }
    })
    const events = shown.events.map((event) => ({
      ...event,
      at_ms: Date.parse(event.at),
      x: minuteOf(Date.parse(event.at)),
    }))
    return {
      dayMinutes,
      ticks: hourTicks(start, shown.end_ms, shown.timezone),
      runs: runs(shown.spans, start, dayMinutes),
      candidates,
      events,
      moments: Object.fromEntries(shown.moments.map((m) => [m.id, m])),
    }
  }, [shown])

  // What each row holds at the cursor, for the line under it.
  const at = hover != null && shown ? shown.start_ms + hover * 60_000 : null
  const readings = useMemo(() => {
    if (at == null || !drawn) return {}
    const minute = shown.spans
      .flatMap((span) => span.segments)
      .find((m) => m.start_ms <= at && at < m.start_ms + m.duration_ms)
    const found = drawn.candidates.filter(
      (row) => hover >= row.x - 1 && hover <= row.x + row.width + 1
    )
    const done = drawn.events.filter(
      (event) => Math.abs(event.x - hover) <= NEAR_MINUTES
    )
    const state = minute ? stateOf(minute) : null
    return {
      recording: !minute
        ? 'not recording'
        : state === 'speech'
        ? `speech found (${Math.round((minute.speech_ms || 0) / 1000)} s of it)`
        : recording[state].label,
      moments: found.length
        ? found
            .map((row) => `“${row.quote}” (${outcomeOf(row.outcome).label})`)
            .join(' · ')
        : 'none here',
      activity: done.length
        ? done
            .map(
              (event) =>
                `${EVENT_LABELS[event.kind]} at ${clock12(event.at_ms, zone)}${
                  event.kind === 'friend' && event.username
                    ? ` (@${event.username})`
                    : ''
                }`
            )
            .join(' · ')
        : 'nothing here',
    }
  }, [at, hover, drawn, shown, recording, zone])

  const step = (delta) => {
    const next = days[index + delta]
    if (next) onDay(next.day)
  }
  // The day's last span: Stream Ingest's list starts there and runs back
  // through the day, each span with how it ended.
  const lastSpan = shown?.spans.length
    ? shown.spans[shown.spans.length - 1].start_ms
    : null
  const row = (title) => ({
    drawn,
    hover,
    onHover: setHover,
    title,
  })

  return (
    <Box
      sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          aria-label="previous day"
          disabled={index <= 0}
          onClick={() => step(-1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2">
          {dayjs(day).format('ddd MMM D')} · {zone}
        </Typography>
        <IconButton
          size="small"
          aria-label="next day"
          disabled={index < 0 || index >= days.length - 1}
          onClick={() => step(1)}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {at != null && (
          <Typography variant="subtitle2" sx={{ ml: 1 }}>
            {clock12(at, zone)}
          </Typography>
        )}
        {!shown && !error && <CircularProgress size={14} sx={{ ml: 1 }} />}
        <Box sx={{ flex: 1 }} />
        {lastSpan != null && (
          <Button
            size="small"
            component={Link}
            to={routerHeardPath({
              userId,
              username,
              spanStartMs: lastSpan,
            })}
            sx={{ textTransform: 'none' }}
          >
            The day’s spans on Stream Ingest
          </Button>
        )}
        <Tooltip title="Close the day">
          <IconButton size="small" onClick={() => onDay(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {error && (
        <Alert severity="error" sx={{ my: 1 }}>
          {error}
        </Alert>
      )}
      {shown && drawn && (
        <>
          <Line>
            {fmtStreamed(shown.totals.recorded_ms)} recorded ·{' '}
            {fmtStreamed(shown.totals.with_speech_ms)} in minutes with speech ·{' '}
            {plural(shown.totals.candidates, 'moment')} found ·{' '}
            {shown.totals.cut} cut
          </Line>
          <Row
            {...row('Recording')}
            height={24}
            items={Object.values(recording)}
            reading={readings.recording}
          >
            {drawn.runs.map((run) => (
              <rect
                key={run.x}
                x={run.x}
                y={0}
                width={Math.max(0.5, run.end - run.x)}
                height={24}
                fill={recording[run.state].color}
              />
            ))}
          </Row>
          <Row
            {...row('Moments')}
            height={16}
            items={[
              { label: 'cut', color: cutColor },
              { label: 'found, not cut', color: notCut },
            ]}
            reading={readings.moments}
          >
            {drawn.candidates.map((found) => {
              const cut = found.outcome === 'cut'
              return (
                <rect
                  key={`${found.start_ms}-${found.end_ms}`}
                  x={found.x}
                  y={cut ? 0 : 8}
                  width={found.width}
                  height={cut ? 16 : 8}
                  fill={cut ? cutColor : notCut}
                />
              )
            })}
          </Row>
          <Row
            {...row('Activity')}
            height={16}
            items={[
              { label: 'pushed', color: colors.moments, tick: true },
              { label: 'comment', color: colors.comments, tick: true },
              { label: 'friend', color: colors.friends, tick: true },
            ]}
            reading={readings.activity}
          >
            {drawn.events.map((event) => (
              <line
                key={`${event.kind}-${event.at}-${
                  event.moment_id || event.user_id
                }`}
                x1={event.x}
                x2={event.x}
                y1={0}
                y2={16}
                stroke={eventColor[event.kind]}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </Row>
          <Box
            sx={{
              position: 'relative',
              height: 16,
              fontSize: 11,
              color: 'text.secondary',
            }}
          >
            {drawn.ticks.map((tick) => (
              <Box
                key={tick.x}
                component="span"
                sx={{
                  position: 'absolute',
                  left: `${(tick.x / drawn.dayMinutes) * 100}%`,
                }}
              >
                {tick.hour}
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Section
              title="Found in the day"
              summary={
                <Line>
                  {plural(shown.totals.candidates, 'moment')} found ·{' '}
                  {shown.totals.cut} cut
                </Line>
              }
              open={foundOpen}
              onToggle={() => setFoundOpen((open) => !open)}
            >
              {shown.candidates.length === 0 && (
                <Line>The selector found nothing in this day.</Line>
              )}
              {[...shown.candidates]
                .sort((a, b) => a.start_ms - b.start_ms)
                .map((found) => {
                  const stored = found.moment_id
                    ? drawn.moments[found.moment_id]
                    : null
                  const outcome = outcomeOf(found.outcome)
                  return (
                    <Box
                      key={`${found.start_ms}-${found.end_ms}`}
                      sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
                    >
                      <Line>{clock(found.start_ms, zone)}</Line>
                      <Chip
                        size="small"
                        color={outcome.color}
                        variant={
                          found.outcome === 'cut' ? 'filled' : 'outlined'
                        }
                        label={outcome.label}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        “{found.quote}”
                        {stored && (
                          <Line>
                            {' '}
                            · {stored.standing}
                            {stored.notified_at &&
                              ` · pushed ${clock(
                                Date.parse(stored.notified_at),
                                zone
                              )}`}
                            {stored.fetched_at &&
                              ` · pulled ${clock(
                                Date.parse(stored.fetched_at),
                                zone
                              )}`}
                          </Line>
                        )}
                      </Typography>
                    </Box>
                  )
                })}
            </Section>
            <Section
              title="What they did"
              summary={<Line>{plural(drawn.events.length, 'event')}</Line>}
              open={didOpen}
              onToggle={() => setDidOpen((open) => !open)}
            >
              {drawn.events.length === 0 && (
                <Line>No pushes, comments or friends this day.</Line>
              )}
              {drawn.events.map((event) => (
                <Box
                  key={`${event.kind}-${event.at}-${
                    event.moment_id || event.user_id
                  }`}
                  sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                >
                  <Line>{clock(event.at_ms, zone)}</Line>
                  <Box
                    sx={{
                      width: 2,
                      height: 12,
                      bgcolor: eventColor[event.kind],
                    }}
                  />
                  <Typography variant="body2">
                    {EVENT_LABELS[event.kind]}
                    {event.kind === 'friend' && event.username
                      ? ` @${event.username}`
                      : ''}
                  </Typography>
                </Box>
              ))}
            </Section>
          </Box>
        </>
      )}
    </Box>
  )
}

export default UserDay
